import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import SiteRenderer from "@/components/SiteRenderer";
import DesktopRecommendedNotice from "@/components/DesktopRecommendedNotice";
import { ArrowLeft, Sparkles, Loader2, Send, Rocket, Eye, Copy, Globe, Monitor, Tablet, Smartphone, Settings, X } from "lucide-react";
import { toast } from "sonner";

const DEVICE = {
  desktop: "100%",
  tablet: "820px",
  mobile: "400px",
};

export default function WebsiteEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const [site, setSite] = useState(null);
  const [chat, setChat] = useState([]);   // [{role:'user'|'ai', text}]
  const [instr, setInstr] = useState("");
  const [busy, setBusy] = useState(false);
  const [device, setDevice] = useState("desktop");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const chatEndRef = useRef(null);

  const load = () => api.get(`/websites/${id}`).then((r) => setSite(r.data)).catch(() => nav("/websites"));
  useEffect(() => { load(); }, [id]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chat, busy]);

  const aiEdit = async () => {
    if (!instr.trim()) return;
    const msg = instr;
    setInstr("");
    setChat((c) => [...c, { role: "user", text: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post(`/websites/${id}/ai-edit`, { instruction: msg });
      setSite(data);
      setChat((c) => [...c, { role: "ai", text: "Applied. Reviewing your website preview." }]);
      refreshUser();
    } catch (e) {
      const err = e?.response?.data?.detail || "Edit failed";
      setChat((c) => [...c, { role: "ai", text: `Error: ${err}` }]);
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/websites/${id}/publish`, {});
      setSite(data);
      toast.success("Website published");
    } catch (e) {
      toast.error("Publish failed");
    } finally {
      setBusy(false);
    }
  };

  const unpublish = async () => {
    await api.post(`/websites/${id}/unpublish`);
    load();
    toast.success("Unpublished");
  };

  const publicUrl = site?.published ? `${window.location.origin}/site/${site.slug}` : null;

  if (!site) return <div className="p-8 text-white/40 text-sm">Loading…</div>;

  return (
    <div className="px-4 sm:px-6 py-4 sm:py-6 max-w-[1600px] mx-auto kr-page">
      <DesktopRecommendedNotice>
        The chat-driven site editor is optimized for a larger screen. On mobile you can still preview, publish, view live, and change settings.
      </DesktopRecommendedNotice>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <Link to="/websites" className="inline-flex items-center text-sm text-white/50 hover:text-white kr-link" data-testid="back-to-websites"><ArrowLeft className="h-4 w-4 mr-1" /> All websites</Link>
        <div className="flex items-center gap-2">
          {publicUrl && (
            <a href={publicUrl} target="_blank" rel="noreferrer">
              <Button variant="outline" className="border-white/10 hover:bg-white/5 hover:text-white" data-testid="view-live-btn"><Eye className="h-4 w-4 mr-2" /> View live</Button>
            </a>
          )}
          <Button onClick={() => setSettingsOpen(!settingsOpen)} variant="outline" className="border-white/10 hover:bg-white/5 hover:text-white" data-testid="site-settings-btn"><Settings className="h-4 w-4" /></Button>
          {site.published ? (
            <Button onClick={unpublish} variant="outline" className="border-white/10 hover:bg-white/5 hover:text-white" data-testid="unpublish-btn">Unpublish</Button>
          ) : (
            <Button onClick={publish} disabled={busy} className="bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 rounded-full" data-testid="publish-btn"><Rocket className="h-4 w-4 mr-2" /> Publish</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Left AI chat panel */}
        <div className="col-span-12 lg:col-span-3 kr-card p-4 flex flex-col" style={{ height: "calc(100vh - 160px)" }}>
          <div className="kr-overline flex items-center gap-2"><Sparkles className="h-3.5 w-3.5 text-[#00E5FF]" /> AI EDITOR</div>
          <div className="mt-1 font-display text-lg truncate" data-testid="site-name">{site.name}</div>
          <div className="text-[10px] text-white/40 font-mono truncate">/{site.slug}</div>

          <div className="mt-4 flex-1 overflow-y-auto space-y-3 pr-1">
            {chat.length === 0 && (
              <div className="text-xs text-white/40 space-y-2">
                <div>Try:</div>
                {["Make the hero more premium", "Add a testimonials section", "Change primary color to warm orange", "Add a booking form"].map((s) => (
                  <button key={s} onClick={() => setInstr(s)} className="block w-full text-left px-3 py-2 border border-white/10 hover:bg-white/5 rounded-sm" data-testid={`suggest-${s.slice(0, 10)}`}>{s}</button>
                ))}
              </div>
            )}
            {chat.map((m, i) => (
              <div key={i} className={`p-3 border ${m.role === "user" ? "border-[#00E5FF]/30 bg-[#00E5FF]/5" : "border-white/10 bg-white/[0.02]"} rounded-sm`}>
                <div className="kr-overline mb-1">{m.role === "user" ? "YOU" : "AI"}</div>
                <div className="text-sm text-white/85 whitespace-pre-wrap">{m.text}</div>
              </div>
            ))}
            {busy && (
              <div className="p-3 border border-white/10 bg-white/[0.02] rounded-sm">
                <div className="kr-overline mb-1">AI</div>
                <div className="flex items-center gap-2 text-sm text-white/60"><Loader2 className="h-3 w-3 animate-spin" /> Rewriting website…</div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          <div className="mt-3 border-t border-white/5 pt-3">
            <Textarea data-testid="ai-edit-input" rows={3} value={instr} onChange={(e) => setInstr(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) aiEdit(); }} placeholder="Describe a change…" className="bg-[#050505] border-white/10 resize-none text-sm" />
            <Button onClick={aiEdit} disabled={busy || !instr.trim()} className="w-full mt-2 bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90" data-testid="ai-edit-send"><Send className="h-3.5 w-3.5 mr-2" /> Apply · 3 cr</Button>
          </div>
        </div>

        {/* Right preview */}
        <div className="col-span-12 lg:col-span-9">
          <div className="kr-card overflow-hidden" style={{ height: "calc(100vh - 160px)" }}>
            <div className="h-11 border-b border-white/5 flex items-center gap-2 px-3 bg-[#050505]">
              <div className="flex gap-1">
                <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
                <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
                <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
              </div>
              <div className="ml-4 flex-1 text-xs text-white/40 font-mono truncate">{publicUrl || `preview://alphacore/${site.slug}`}</div>
              <div className="flex gap-1">
                <button onClick={() => setDevice("desktop")} className={`p-1.5 ${device === "desktop" ? "text-[#00E5FF]" : "text-white/40 hover:text-white"}`} data-testid="device-desktop"><Monitor className="h-4 w-4" /></button>
                <button onClick={() => setDevice("tablet")} className={`p-1.5 ${device === "tablet" ? "text-[#00E5FF]" : "text-white/40 hover:text-white"}`} data-testid="device-tablet"><Tablet className="h-4 w-4" /></button>
                <button onClick={() => setDevice("mobile")} className={`p-1.5 ${device === "mobile" ? "text-[#00E5FF]" : "text-white/40 hover:text-white"}`} data-testid="device-mobile"><Smartphone className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="h-[calc(100%-2.75rem)] overflow-auto bg-black flex justify-center" data-testid="site-preview">
              <div className="w-full transition-all bg-[#050505] shadow-2xl" style={{ maxWidth: DEVICE[device] }}>
                <SiteRenderer site={site} embedded />
              </div>
            </div>
          </div>
        </div>
      </div>

      {settingsOpen && <SettingsPanel site={site} setSite={setSite} onClose={() => setSettingsOpen(false)} />}
    </div>
  );
}

function SettingsPanel({ site, setSite, onClose }) {
  const [name, setName] = useState(site.name || "");
  const [seo, setSeo] = useState(site.seo || {});
  const save = async () => {
    const { data } = await api.patch(`/websites/${site.website_id}`, { name, seo });
    setSite(data);
    toast.success("Saved");
    onClose();
  };
  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-end lg:items-center justify-center" onClick={onClose}>
      <div className="bg-[#0A0A0E] border border-white/10 w-full max-w-lg p-6 rounded-t-lg lg:rounded-lg" onClick={(e) => e.stopPropagation()} data-testid="site-settings-panel">
        <div className="flex items-center justify-between">
          <div className="font-display text-lg">Site settings</div>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="mt-5 space-y-4">
          <div>
            <div className="kr-overline mb-2">Name</div>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-[#050505] border-white/10" data-testid="settings-name-input" />
          </div>
          <div>
            <div className="kr-overline mb-2">SEO Title</div>
            <Input value={seo.title || ""} onChange={(e) => setSeo({ ...seo, title: e.target.value })} className="bg-[#050505] border-white/10" data-testid="settings-seo-title" />
          </div>
          <div>
            <div className="kr-overline mb-2">SEO Description</div>
            <Textarea rows={3} value={seo.description || ""} onChange={(e) => setSeo({ ...seo, description: e.target.value })} className="bg-[#050505] border-white/10 resize-none" data-testid="settings-seo-desc" />
          </div>
        </div>
        <Button onClick={save} className="w-full mt-6 bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90" data-testid="settings-save-btn">Save</Button>
      </div>
    </div>
  );
}
